import { safeResponseMessage } from '../../utils/safeResponseMessage'

/**
 * 休日出勤報告の「保存で送る中身」を1か所にまとめる。
 *
 * 同じ t_holiday_work の1行を、3つの画面が別々の書き方で扱っていた。
 *
 *   新規 RegisterInput  画面の値 + ":00"   → "09:00:00"
 *   修正 UpdateInput    画面の値のまま      → 触らなければ "09:00:00" / 触ると "09:00"
 *   詳細 Info           substring(0, 5)    → "09:00"
 *
 * 修正が2通りになるのは、GET が "HH:mm:ss" を返すのに
 * <input type="time"> は利用者が触ると "HH:mm" を返すため。
 * Backend の HolidayWorkInputValidator が両方を受けるので今は動いている。
 * ただ「送る形」を画面ごとに決めている限り、片方だけ直せばまたずれる。
 *
 * とくに `+ ":00"` は文字列の継ぎ足しで、未入力だと ":00" になる。
 * ":00" は空ではないので Backend の @NotBlank を通ってしまい、
 * 「開始予定時間を入力してください。」ではなく
 * 「開始予定時間は00:00から23:59までの範囲で入力してください。」が返る。
 * setInteractionMode('eager') は一度も触っていない項目を検証しないため、
 * 未入力でも報告ボタンは押せる。実際に踏める道になっていた。
 */

/*
 * "09:00" と "09:00:00" のどちらでも受ける。
 *
 * 桁数は Backend の HolidayWorkInputValidator と同じにする。実物で確かめた
 * ところ、あちらは "9:00" を落とす（LocalTime.parse の HH:mm は厳密）。
 * こちらで "09:00" へ詰めてしまうと、画面だけが受け付ける形ができる。
 * <input type="time"> は必ず0詰めで返すので、詰める処理は要らない。
 */
const TIME_PATTERN = /^(\d{2}):(\d{2})(?::(\d{2}))?$/

/**
 * <input type="time"> へ渡す形（HH:mm）にする。
 *
 * GET が返す "09:00:00" をそのまま v-model へ入れると、
 * 利用者が触ったかどうかで保存時の値が変わってしまう。
 * 画面が持つ形を先に揃えておく。
 *
 * 読めない値は勝手に捨てない。そのまま返して、判断は Backend に任せる。
 */
export function toTimeInputValue (value) {
  if (value === null || value === undefined) return ''
  const text = String(value).trim()
  const matched = TIME_PATTERN.exec(text)
  if (!matched) return text
  return matched[1] + ':' + matched[2]
}

/**
 * 送信する形（HH:mm:ss）にする。
 *
 * 未入力は未入力のまま送る。ここで ":00" のような形を作ると、
 * Backend の必須チェックを通り抜けて別の文言が返る。
 */
export function toPayloadTime (value) {
  if (value === null || value === undefined) return ''
  const text = String(value).trim()
  if (!text) return ''
  const matched = TIME_PATTERN.exec(text)
  // 読めない値は勝手に直さない。Backend が文言つきで断る。
  if (!matched) return text
  return matched[1] + ':' + matched[2] + ':' + (matched[3] || '00')
}

/**
 * 新規と修正の両方が、これで本文を組み立てる。
 *
 * @param {{date: string, startTime: string, endTime: string, reason: string}} input
 * @returns {URLSearchParams}
 */
export function buildSaveParams (input) {
  const source = input || {}
  const params = new URLSearchParams()
  params.append('date', String(source.date === null || source.date === undefined ? '' : source.date).trim())
  params.append('start_time', toPayloadTime(source.startTime))
  params.append('end_time', toPayloadTime(source.endTime))
  // 発生理由は前後の空白も利用者が入れたもの。ここでは削らない。
  params.append('reason', source.reason === null || source.reason === undefined ? '' : String(source.reason))
  return params
}

/**
 * 保存が断られたとき、利用者へ出す文言。
 *
 * Backend は入力の不備を 400 + {message: "日付を入力してください。"} で返す
 * （MyRestControllerAdvice#handleIllegalArgument）。この文言は項目ごとに
 * 書き分けてあるのに、画面が中身を見ずに「システムエラーです。」と出していた。
 */
export function saveErrorMessage (error, fallback) {
  return safeResponseMessage(error, fallback)
}

/**
 * 障害ログ（/error/frontError）へ積むべき失敗かどうか。
 *
 * 入力の不備（400）や、他人の報告を触った（409）は障害ではない。
 * 積むと本当の障害が埋もれる。通信断（response なし）と 5xx だけ残す。
 */
export function isReportableFailure (error) {
  const response = error && error.response
  if (!response) return true
  const status = Number(response.status)
  return !(status >= 400 && status < 500)
}
